import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, ChevronRight, Loader2 } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { vanDesignApi } from "@/api/vanDesign";
import { VanHeader, NCard, NBtn, SegGroup, MonoBadge } from "./vanUI";
import VaultPickButton from "@/components/mist/VaultPickButton";

const STEPS = ["Van", "Photos", "Use"];

export default function VanDesignIntake({ onBack }) {
  const { project, setVan, setScreen, photos, setPhotos } = useVanDesign();
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState(project.van?.templateId || "");
  const [custom, setCustom] = useState({ length: "", width: "", height: "" });
  const [region, setRegion] = useState(project.van?.region || "Australia");
  const [useCase, setUseCase] = useState(project.goals?.usage || "part_time");
  const fileRef = useRef(null);

  useEffect(() => { vanDesignApi.templates().then((d) => setTemplates(d.templates || [])).catch(() => {}); }, []);

  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    const b64s = await Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setPhotos(b64s);
    if (b64s.length) toast.success(`${b64s.length} photo(s) added`);
  };

  const draft = async () => {
    const tpl = templates.find((t) => t.id === tplId);
    let interior = tpl?.interior;
    if (!tpl && custom.length && custom.width) {
      interior = { length: +custom.length, width: +custom.width, height: +(custom.height || 1850) };
    }
    if (!interior) { toast.error("Pick a van template or enter custom length + width"); return; }
    setVan({
      templateId: tpl?.id || null, make: tpl?.make || "Custom", model: tpl?.model || "Van",
      variant: tpl?.variant || "Custom dims", year: tpl?.year_from || 2019, region,
      interior, doors: tpl?.doors || null,
    });
    setScreen("goals");
  };

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-intake">
      <VanHeader title="Project Setup" sub="Step 1 of 3 · Intake" onBack={onBack} step="1 / 3" testid="van-intake-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        {/* progress strip */}
        <div className="grid grid-cols-3 gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-2 rounded-full border-2 border-black ${i === 0 ? "bg-[#D4AF37]" : "bg-white"}`} />
          ))}
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-2">Choose your van</p>
          <div className="grid grid-cols-2 gap-2">
            {templates.map((t) => (
              <button key={t.id} onClick={() => setTplId(t.id)} data-testid={`van-tpl-${t.id}`}
                className={`text-left p-2.5 rounded-xl border-2 border-black ${tplId === t.id ? "bg-black text-[#FDFBF7] shadow-[3px_3px_0px_#000]" : "bg-white text-black"}`}>
                <p className="font-serif text-sm leading-tight">{t.model}</p>
                <p className="font-mono text-[8px] uppercase opacity-70 mt-0.5">{t.make} · {t.variant}</p>
                <p className="font-mono text-[8px] opacity-60 mt-1">{t.interior.length}×{t.interior.width}mm</p>
              </button>
            ))}
          </div>
        </div>

        <NCard className="p-3">
          <p className="font-mono text-[9px] uppercase tracking-widest text-black/60 mb-2">Or custom dimensions (mm)</p>
          <div className="grid grid-cols-3 gap-2">
            {["length", "width", "height"].map((k) => (
              <input key={k} value={custom[k]} inputMode="numeric" placeholder={k}
                onChange={(e) => { setCustom((c) => ({ ...c, [k]: e.target.value.replace(/\D/g, "") })); setTplId(""); }}
                data-testid={`van-custom-${k}`}
                className="w-full bg-white border-2 border-black rounded-lg px-2 py-2 font-mono text-xs text-black outline-none" />
            ))}
          </div>
          <p className="font-mono text-[9px] text-black/50 mt-1.5">Don’t know exact numbers? Pick a template and refine later.</p>
        </NCard>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-2">Interior photos (optional)</p>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickPhotos} data-testid="van-photo-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="van-photo-btn"
            className="w-full border-2 border-dashed border-black rounded-xl bg-white py-4 flex flex-col items-center gap-1 shadow-[3px_3px_0px_#000]">
            <Camera size={20} /><span className="font-mono text-[10px] uppercase tracking-widest">Upload up to 3 photos</span>
            <span className="font-mono text-[8px] text-black/50">We estimate fit + fixtures from your photos</span>
          </button>
          <div className="mt-2 flex justify-center">
            <VaultPickButton onFile={(f) => pickPhotos({ target: { files: [f], value: "" } })} bucket="images" testid="van-photo-vault" label="Or pick from your Vault"
              className="flex items-center justify-center gap-1.5 border-2 border-black bg-black text-[#00F5D4] px-3 py-2 font-mono text-[9px] font-black uppercase tracking-widest active:translate-y-[1px]" />
          </div>
          {photos.length > 0 && (
            <div className="flex gap-2 mt-2">
              {photos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg border-2 border-black overflow-hidden">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-black text-white w-4 h-4 grid place-items-center"><X size={9} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-2">Intended use</p>
          <SegGroup testid="van-usecase"
            options={[{ value: "weekend", label: "Weekend" }, { value: "part_time", label: "Part-time" }, { value: "full_time", label: "Full-time" }]}
            value={useCase} onChange={setUseCase} />
        </div>
        <div className="flex gap-2 items-center">
          <MonoBadge>Region</MonoBadge>
          <input value={region} onChange={(e) => setRegion(e.target.value)} data-testid="van-region"
            className="flex-1 bg-white border-2 border-black rounded-lg px-3 py-2 font-mono text-xs text-black outline-none" />
        </div>
        <div className="h-2" />
      </div>
      <div className="shrink-0 border-t-2 border-black p-3 bg-[#FDFBF7]">
        <NBtn variant="gold" onClick={() => { useCase && (project.goals.usage = useCase); draft(); }} testid="van-intake-next">
          Continue to Goals <ChevronRight size={15} />
        </NBtn>
      </div>
    </div>
  );
}
